import { useCallback, useMemo, useState } from 'react';

import { useDebounceCallback } from '@xemplo/hooks';

import { createListFilter } from '../filter/list-filter.helpers';
import { isValueItem } from '../list.helpers';
import { ListReservedValues, ListValue, StatefulList } from '../stateful-list.types';

type UseItemsSearchProps = {
  items: StatefulList;
  initialSearch?: string;
  searchable?: boolean;
};

export function useItemsSearch({
  items,
  initialSearch,
  searchable = true, // needs to be true for compatibility with existing implementations
}: UseItemsSearchProps) {
  const [search, setSearch] = useState(initialSearch ?? '');

  // Debounced search term for filtering to avoid excessive computations on fast input changes
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch ?? '');
  const updateDebouncedSearch = useDebounceCallback(setDebouncedSearch, 300);

  const filterItems = useCallback(
    (searchTerm: string) => {
      const renderItems = searchable ? createListFilter(searchTerm).filter(items) : items;
      const valueItems = renderItems.groups.flatMap((g) => g.items).filter(isValueItem);
      const valueItemMap = new Map(valueItems.map((item) => [item.value, item]));
      const isNoResult =
        renderItems.groups.length === 1 && // Checking for length to avoid potential performance issues with large lists
        renderItems.groups
          .flatMap((g) => g.items)
          .filter(isValueItem)
          .every((item) => item.value === ListReservedValues.noResult);
      return { renderItems, valueItems, valueItemMap, isNoResult };
    },
    [items, searchable],
  );

  /**
   * Handle search input changes and apply debouncing
   * to avoid filtering on every keystroke.
   */
  const handleSetSearch = useCallback(
    (newSearch: string) => {
      setSearch(newSearch);
      updateDebouncedSearch(newSearch);
    },
    [updateDebouncedSearch],
  );

  /**
   * There are certain cases where we need to bypass debouncing.
   * This function allows immediate updates to the search term
   * and triggers the filtering logic without delay.
   */
  const handleInstanceSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    setDebouncedSearch(newSearch);
  }, []);

  /**
   * Updates only the visible search text without touching debouncedSearch.
   * Use this for programmatic label restoration (e.g. syncing an RHF value
   * after async options load) so the items list is not filtered as a side-effect.
   */
  const handleDisplayOnly = useCallback((display: string) => {
    setSearch(display);
  }, []);

  /** Unfiltered value→item map, rebuilt only when items or searchable changes. */
  const unfilteredValueItemMap = useMemo(
    () => filterItems('').valueItemMap,
    [filterItems],
  );

  /**
   * Returns the label for a value from the full, unfiltered item list.
   * Unlike valueItemMap (which reflects the current search filter), this
   * always scans all items — so it remains stable when debouncedSearch changes
   * and only changes when the items prop itself changes.
   */
  const getItemLabel = useCallback(
    (value: ListValue): string => unfilteredValueItemMap.get(value)?.label ?? '',
    [unfilteredValueItemMap],
  );

  /** Memoized filtered items based on the debounced search term */
  const { renderItems, valueItems, valueItemMap, isNoResult } = useMemo(
    () => filterItems(debouncedSearch),
    [filterItems, debouncedSearch],
  );

  return {
    renderItems,
    valueItems,
    valueItemMap, // For quick access to items by value
    isNoResult,
    search,
    setSearch: handleSetSearch,
    instantSetSearch: handleInstanceSearch, // Immediate setSearch without debounce - for programmatic updates
    setSearchDisplayOnly: handleDisplayOnly, // Sets display text only, does not filter the list
    getItemLabel, // Stable label lookup — only changes when items prop changes, not when search changes
  };
}
